interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  // True while an async toggle call (e.g. ChannelShareScreen's
  // toggleShareLink) is in flight - distinct from `disabled` alone, which
  // only dims the switch to 50% opacity with no other visual change. A
  // slow request (a real WSL workflow round trip, not instant) otherwise
  // looks identical to the click having done nothing at all, which reads
  // as broken until the promise eventually resolves - this spinner is the
  // difference between "working on it" and "stuck".
  loading?: boolean;
}

export function Toggle({ label, checked, onChange, disabled, loading }: ToggleProps) {
  return (
    <label className={`flex items-center justify-between gap-4 py-1 select-none ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <span className="flex items-center gap-2 text-[15px]">
        {label}
        {loading && (
          <svg className="animate-spin h-3.5 w-3.5 text-black/40 dark:text-white/40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        )}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative rounded-full transition-colors shrink-0 ${
          checked ? 'bg-blue-600' : 'bg-black/15 dark:bg-white/20'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
        style={{ height: 26, width: 44 }}
      >
        <span
          className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
          style={{ transform: checked ? 'translateX(18px)' : 'translateX(0)' }}
        />
      </button>
    </label>
  );
}
